import { onEffectEndEvent } from '../../../shared/utilities/effectsEndEventHandler';
import { SwipeEventDispatcher } from '../../../shared/utilities/swipe-event-dispatcher';

class ItemSlider {
	constructor(slider, options = { isOnPageNavParent: false }) {
		/* isOnPageNavParent dependency can be removed once we normalize UI for detailed Tab and OnPageNav
		 and remove the need for this class to be aware of OnPageNav. */
		this.config = {
			...options,
			slider,
			leftSlider: slider.querySelector('.v2-item-slider__thumb--left'),
			rightSlider: slider.querySelector('.v2-item-slider__thumb--right'),
			sliderRoot: slider.querySelector('.v2-item-slider__root'),
			sliderItems: slider.querySelector('.v2-item-slider__items'),
			itemList: Array.from(slider.querySelectorAll('.v2-item-slider__item'))
		};
		// check for rtl orientation
		this.isRtl = document.dir === 'rtl';
		this.itemList = Array.from(this.config.sliderRoot.querySelectorAll('.v2-item-slider__item'));
		this.itemPaddingSpace = this.getItemPadding();
		this.viewPointers = {
			start: 0,
			end: this.itemList.length - 1
		};
		this.checkOverlappingLinkOnRight();
		this.changeSlidersState();
		// add event listener to sliders
		this.addEventListeners();
	}

	setWidth(width) {
		this.config.slider.style.width = width + 'px';
	}

	getItemPadding() {
		const linkStyle = window.getComputedStyle(this.itemList[0]);
		return this.isRtl
			? Number.parseInt(linkStyle.paddingLeft, 10)
			: Number.parseInt(linkStyle.paddingRight, 10);
	}

	canRun(e, cb) {
		const target = e.target.classList.contains('v2-item-slider__thumb')
			? e.target
			: e.target.closest('.v2-item-slider__thumb');
		target.classList.contains('disabled') ? e.stopPropagation() : cb();
	}

	addEventListeners() {
		const onPrevious = this.showNewItems.bind(this, false);
		const onNext = this.showNewItems.bind(this, true);
		const swipeEvtHandler = new SwipeEventDispatcher(this.config.sliderRoot);

		swipeEvtHandler.on('SWIPE_LEFT', onNext);
		swipeEvtHandler.on('SWIPE_RIGHT', onPrevious);
		this.config.leftSlider.addEventListener('click', e => this.canRun(e, onPrevious), false);
		this.config.rightSlider.addEventListener('click', e => this.canRun(e, onNext), false);
	}

	/*
	 This function checks overlapping on end and update pointers
	*/
	checkOverlappingLinkOnRight() {
		let sum = 0;
		const menuElementWidth = this.getWidth(this.config.sliderRoot, true, false);
		for (let i = this.viewPointers.start; i < this.itemList.length; i++) {
			const link = this.itemList[i];
			const width = this.getWidth(link, true, true);
			sum += width;
			const linkVisible = sum <= menuElementWidth;
			if (!linkVisible) {
				if (sum - this.itemPaddingSpace <= menuElementWidth) {
					// visible but padding is overlapping, no need to hide this
					this.updatePointer('end', i);
					break;
				} else {
					// overlapping
					this.updatePointer('end', i - 1);
					break;
				}
			}
		}
	}

	getObject(index) {
		const linkElem = this.itemList[index];
		return {
			index,
			link: linkElem,
			width: this.getWidth(linkElem, true, true)
		};
	}

	disableSliders(isDisabled) {
		if (isDisabled) {
			this.config.leftSlider.classList.add('disabled');
			this.config.rightSlider.classList.add('disabled');
		} else {
			this.config.leftSlider.classList.remove('disabled');
			this.config.rightSlider.classList.remove('disabled');
		}
	}

	showNewItems(isNext) {
		// return if already last item
		if (isNext && this.viewPointers.end === this.itemList.length - 1) {
			return;
		}
		// return if already first item
		if (!isNext && this.viewPointers.start === 0) {
			return;
		}
		// Disabling controls till animation completes.
		this.disableSliders(true);

		// It shows previous items or next items based on click on left arrow or right arrow
		const incomingObject = isNext
			? this.getObject(this.viewPointers.end + 1)
			: this.getObject(this.viewPointers.start - 1);
		const outgoingObject = isNext
			? this.getObject(this.viewPointers.start)
			: this.getObject(this.viewPointers.end);

		const leftSliderWidth = this.getWidth(this.config.leftSlider, true, true);

		const initialState = {
			leftPositionChange: isNext
				? outgoingObject.width + leftSliderWidth
				: incomingObject.width - leftSliderWidth,
			outgoingLinks: [outgoingObject],
			incomingLinks: [incomingObject]
		};

		const finalState = this.getFinalState(initialState, isNext);

		this.updateViewWindow(isNext, finalState);
	}

	/*
		This function calculates shift in leftposition and multiple outgoing items from  view area
		  - from right when left arrow is pressed
		  - from left when right arrow is pressed
	*/
	multipleOutgoingsFromStart(initial, difference) {
		let leftChange = initial.leftPositionChange;
		let diff = difference;
		const start = initial.outgoingLinks[0].index + 1;
		const end = initial.incomingLinks[0].index;
		let i;
		for (i = start; i < end; i++) {
			const outgoingObject = this.getObject(i);
			initial.outgoingLinks.push(outgoingObject);

			diff -= outgoingObject.width;

			leftChange += outgoingObject.width;

			if (diff <= this.itemPaddingSpace) {
				break;
			}
		}
		this.updatePointer('start', i + 1);
		this.updatePointer('end', end);

		return { leftPositionChange: leftChange, outgoingLinks: initial.outgoingLinks };
	}

	multipleOutgoingsFromEnd(initial, difference) {
		const leftChange = initial.leftPositionChange;
		let diff = difference;
		const start = initial.outgoingLinks[0].index - 1;
		const end = initial.incomingLinks[0].index;
		let i;
		for (i = start; i > end; i--) {
			const outgoingObject = this.getObject(i);
			initial.outgoingLinks.push(outgoingObject);

			diff -= outgoingObject.width;

			if (diff <= this.itemPaddingSpace) {
				break;
			}
		}

		this.updatePointer('start', end);
		this.updatePointer('end', i - 1);

		return { leftPositionChange: leftChange, outgoingLinks: initial.outgoingLinks };
	}

	multipleIncomingsFromStart(initial, difference) {
		const start = initial.incomingLinks[0].index - 1;
		const end = 0;
		let leftChange = initial.leftPositionChange;
		let diff = Math.abs(difference);
		if (start >= 0 && start < this.itemList.length) {
			// this is the valid range for new items
			for (let i = start; i >= end; i--) {
				const nextIncomingItemWidth = this.getWidth(this.itemList[i], true, true);
				if (nextIncomingItemWidth > diff) {
					break;
				}
				const incomingObject = this.getObject(i);

				leftChange += incomingObject.width;
				diff -= Math.abs(initial.leftPositionChange);

				initial.incomingLinks.push(incomingObject);
				this.updatePointer('start', i);
			}
		}

		return { leftPositionChange: leftChange, incomingLinks: initial.incomingLinks };
	}

	multipleIncomingsFromEnd(initial, difference) {
		const start = initial.incomingLinks[0].index + 1;
		const end = this.itemList.length;
		const leftChange = initial.leftPositionChange;
		let diff = difference;
		if (start >= 0 && start < this.itemList.length) {
			// this is the valid range for new items
			for (let i = start; i < end; i++) {
				const nextIncomingItemWidth = this.getWidth(this.itemList[i], true, true);
				if (nextIncomingItemWidth > -diff) {
					break;
				}
				const incomingObject = this.getObject(i);
				diff += incomingObject.width;
				initial.incomingLinks.push(incomingObject);
				this.updatePointer('end', i);
			}
		}

		return { leftPositionChange: leftChange, incomingLinks: initial.incomingLinks };
	}

	/*
		This function calculates final state with
		incoming link items, outgoing link items and change in left position for view area
		on click of left and right arrow
		Also updates pointers.
		param isNext is true when right arrow is clicked
	*/
	getFinalState(initial, isNext) {
		// check if multiple links needs to be added or removed from view
		const blankViewSpace = this.getBlankViewSpace(); // o
		const difference =
			initial.incomingLinks[0].width - (initial.outgoingLinks[0].width + blankViewSpace);

		isNext
			? this.updatePointer('start', this.viewPointers.start + 1)
			: this.updatePointer('start', this.viewPointers.start - 1);
		isNext
			? this.updatePointer('end', this.viewPointers.end + 1)
			: this.updatePointer('end', this.viewPointers.end - 1);

		if (difference >= 0 && difference <= this.itemPaddingSpace) {
			return initial;
		}
		if (difference > this.itemPaddingSpace) {
			// more than one itmes needs to be removed
			const { leftPositionChange, outgoingLinks } = isNext
				? this.multipleOutgoingsFromStart(initial, difference)
				: this.multipleOutgoingsFromEnd(initial, difference);
			return {
				leftPositionChange,
				outgoingLinks,
				incomingLinks: initial.incomingLinks
			};
		}
		// more than one items may be added in view
		const { leftPositionChange, incomingLinks } = isNext
			? this.multipleIncomingsFromEnd(initial, difference)
			: this.multipleIncomingsFromStart(initial, difference);

		return {
			leftPositionChange,
			outgoingLinks: initial.outgoingLinks,
			incomingLinks
		};
	}

	updatePointer(key, val) {
		this.viewPointers[key] = val;
	}

	updateLeftPosition(isNext, leftChange) {
		if (!this.isRtl) {
			const { offsetLeft } = this.config.sliderItems;
			this.config.sliderItems.style.left = isNext
				? `${offsetLeft - leftChange}px`
				: `${offsetLeft + leftChange}px`;
		} else {
			const offsetRight =
				parseInt(window.getComputedStyle(this.config.sliderItems).right, 10) +
				this.getWidth(this.config.leftSlider, true, true);
			this.config.sliderItems.style.right = isNext
				? `${offsetRight - leftChange}px`
				: `${offsetRight + leftChange}px`;
		}
	}

	updateViewWindow(isNext, { leftPositionChange }) {
		this.updateLeftPosition(isNext, leftPositionChange);
		onEffectEndEvent(
			this.config.sliderItems,
			() => {
				this.checkOverlappingLinkOnRight();
				this.changeSlidersState();
			},
			'transition',
			0
		);
	}

	changeSlidersState() {
		this.hideSliderIfAtEdge();
		this.disableSliders(false);
		if (this.config.isOnPageNavParent) {
			this.disableOnPageNavSliderButtons();
		}
	}

	hideSliderIfAtEdge() {
		if (this.viewPointers.start === 0) {
			this.hideSlider(this.config.leftSlider);
		} else {
			this.showSlider(this.config.leftSlider);
		}

		// hide right arrow if last link
		if (this.viewPointers.end === this.itemList.length - 1) {
			this.hideSlider(this.config.rightSlider);
		} else {
			this.showSlider(this.config.rightSlider);
		}
	}

	disableOnPageNavSliderButtons() {
		if (this.viewPointers.start === 0) {
			this.disableSlider(this.config.leftSlider);
		} else {
			this.enableSlider(this.config.leftSlider);
		}

		// disable right arrow if last link
		if (this.viewPointers.end === this.itemList.length - 1) {
			this.disableSlider(this.config.rightSlider);
		} else {
			this.enableSlider(this.config.rightSlider);
		}
	}

	isAnySliderVisible() {
		const hasLeftSlider = this.viewPointers.start > 0;
		const hasRightSlider = this.viewPointers.end < this.itemList.length - 1;

		return hasLeftSlider || hasRightSlider;
	}

	disableSlider(slider) {
		slider.querySelector('.arrow')?.setAttribute('disabled', 'disabled');
		slider.classList.add('v2-item-slider__thumb--disabled');
	}

	enableSlider(slider) {
		slider.querySelector('.arrow')?.removeAttribute('disabled');
		slider.classList.remove('v2-item-slider__thumb--disabled');
	}

	hideSlider(slider) {
		slider.classList.remove('v2-item-slider__thumb--visible');
	}

	showSlider(slider) {
		slider.classList.add('v2-item-slider__thumb--visible');
	}

	getWidth(elem, withPadding, withMargin) {
		const style = window.getComputedStyle(elem);
		let width = elem.offsetWidth || Number.parseFloat(style.width);
		if (!withPadding) {
			width = width - Number.parseFloat(style.paddingLeft) - Number.parseFloat(style.paddingRight);
		}
		if (withMargin) {
			width = width + Number.parseFloat(style.marginLeft) + Number.parseFloat(style.marginRight);
		}
		return width;
	}

	getBlankViewSpace() {
		const totalViewWidth = this.getWidth(this.config.sliderRoot, true, false);
		let visibleItemsWidth = 0;
		let space = 0;
		for (let i = this.viewPointers.start; i <= this.viewPointers.end; i++) {
			const link = this.itemList[i];
			visibleItemsWidth += this.getWidth(link, true, true);
		}
		space = totalViewWidth - visibleItemsWidth;
		return space < 0 ? this.itemPaddingSpace : space + this.itemPaddingSpace;
	}
}

export const itemSlider = (slider, options) => new ItemSlider(slider, options);
